import { useCallback, useEffect, useRef } from "react";

import {
  AnalyticsFieldName,
  RootAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { LogoutReason } from "@/constants/Login";
import useAuth from "@/hooks/useAuth";
import useHelpCenter from "@/hooks/useHelpCenter";
import { ModalLockSources, useModalTracker } from "@/hooks/useModalTracker";
import useUserInactivityTimeout from "@/hooks/useUserInactivityTimeout";
import { useAnalytics } from "@/services/Analytics";
import type {
  BiometricAuthFunction,
  OnBiometricAuthFailureCallback,
} from "@/ui/components/navigation/RootNavigator/useBiometricAuth";
import { DefaultUserInactivityTimeoutMs } from "@/utils/appInactivity.util";

const useInactivityTimeoutHandler = ({
  doBiometricAuth,
  isFirstLaunchSetupDone,
  onBiometricAuthFailure,
}: {
  doBiometricAuth: BiometricAuthFunction;
  isFirstLaunchSetupDone: boolean;
  onBiometricAuthFailure: OnBiometricAuthFailureCallback;
}) => {
  const { track } = useAnalytics();
  const { allowNativeModalsAfterDelay, preventNativeModals } =
    useModalTracker();
  const { hideInAppNotifications } = useHelpCenter();
  const trackRootEvent = useCallback(
    // eslint-disable-next-line max-params
    (event: string, boolProps?: Record<string, boolean>) => {
      track(event, {
        [AnalyticsFieldName.Component]: "RootNavigator",
        [AnalyticsFieldName.InactivityLimitMs]: DefaultUserInactivityTimeoutMs,
        [AnalyticsFieldName.Source]: "RootNavigator",
        ...boolProps,
      });
    },
    [track],
  );
  const { isAuthenticated } = useAuth();

  const onInactivityTimeout = useCallback(async () => {
    const modalLockSource = ModalLockSources.InactivityTimeout;
    trackRootEvent(RootAnalyticsEvent.InactivityTimeout);
    if (!isAuthenticated || !isFirstLaunchSetupDone) {
      trackRootEvent(RootAnalyticsEvent.InactivityIgnored, {
        [AnalyticsFieldName.IsAuthenticated]: isAuthenticated,
        [AnalyticsFieldName.IsFirstLaunchSetupDone]: isFirstLaunchSetupDone,
      });
      return;
    }
    // Close any open native modals
    preventNativeModals(modalLockSource);
    await hideInAppNotifications();
    // biometricAuth() returns false if failure authenticating, OR if biometric authentication not supported/enrolled
    // in any of those cases, default to a full logout
    const response = await doBiometricAuth({
      // prevent navigation to postLoginRouting page after inactivity timeout
      isAppInitialization: false,
    });
    const isSuccess = Boolean(response?.data);
    trackRootEvent(
      isSuccess
        ? RootAnalyticsEvent.InactivityBiometricAuthSuccess
        : RootAnalyticsEvent.InactivityLoggedOut,
    );
    if (isSuccess) {
      // Allow native modals to be shown again
      allowNativeModalsAfterDelay(modalLockSource);
      return;
    }
    // if failure, allowNativeModalsAfterDelay() will get called later, after successful init of the LoginPage
    await onBiometricAuthFailure({
      logoutReason: LogoutReason.InactivityTimeout,
      shouldDisableBiometricAuth: false,
    });
  }, [
    isAuthenticated,
    isFirstLaunchSetupDone,
    doBiometricAuth,
    onBiometricAuthFailure,
    trackRootEvent,
    allowNativeModalsAfterDelay,
    preventNativeModals,
    hideInAppNotifications,
  ]);

  const onInactivityTimeoutRef = useRef(onInactivityTimeout);
  useEffect(() => {
    onInactivityTimeoutRef.current = onInactivityTimeout;
  }, [onInactivityTimeoutRef, onInactivityTimeout]);

  useUserInactivityTimeout({
    onInactivityTimeoutRef,
  });
};

export default useInactivityTimeoutHandler;
